import { useEffect, useRef } from 'react';

interface UseCardExpoOptions {
  onExpo?: () => void;
  threshold?: number;
  rootMargin?: string;
  isActive?: boolean;
}

export const useCardExpo = <T extends HTMLElement>({
  onExpo,
  threshold = 0.5,
  rootMargin = '0px',
  isActive = true,
}: UseCardExpoOptions = {}) => {
  const elementRef = useRef<T | null>(null);
  const hasExposedRef = useRef(false);
  const optionsRef = useRef({ onExpo, threshold, rootMargin });

  // 更新最新的options
  optionsRef.current = { onExpo, threshold, rootMargin };

  useEffect(() => {
    const currentElement = elementRef.current;
    const options = optionsRef.current;

    if (!isActive || !currentElement || !options.onExpo || hasExposedRef.current) {
      return; // 已曝光或曝光条件未满足时直接返回
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 再次检查hasExposedRef以确保安全，尽管外部检查应该足够
            if (!hasExposedRef.current && options.onExpo) {
              options.onExpo();
              hasExposedRef.current = true; // 标记为已曝光
            }
            // 从此观察器实例中取消观察该特定元素
            observer.unobserve(currentElement);
          }
        });
      },
      {
        threshold: options.threshold,
        rootMargin: options.rootMargin,
      },
    );

    observer.observe(currentElement);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [isActive]); // 只在 isActive 变化时重新运行 effect

  return elementRef;
};
